import React, { Component, Fragment } from "react";
import { Column, ColumnFilterElementTemplateOptions } from "primereact/column";
import { Button } from "primereact/button";
import { DataTableToolbar } from "../../components/ui";
import { toastService } from "../../services/toastService";
import { InputSwitch } from "primereact/inputswitch";
import { confirmAction } from "../../utils/confirmHelpers";
import { Tooltip } from "primereact/tooltip";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import moment from "moment";
import ExtendedDataTable from "../../components/ExtendedDataTable";
import { dateBodyTemplate } from "../../utils/columnTemplates";
import { numberFilterMatchModeOptions, stringFilterMatchModeOptions, dateFilterTemplate, statusFilterMatchModeOptions } from "../Paginator";
import { SearchRequestBaseModel, SortOrderType } from "../../models/SearchRequestModel";
import tariffApiService from "../../services/api/tariff/TariffApiService";
import { BillTariffModel, CreateBillTariffRequest, UpdateBillTariffRequest, ScaleTier } from "../../models/BillTariff";
import EditBillTariffModal from "../../components/BillTariff/EditBillTariffModal";
import ViewBillTariffModal from "../../components/BillTariff/ViewBillTariffModal";
import { DEFAULT_CURRENCY, AppConstants } from "../../models/AppConstants";
import { Eye, Pencil, Trash2 } from "lucide-react";

interface BillTariffState {
    billTariffs: BillTariffModel[];
    isLoading: boolean;
    totalRecords: number;
    showDialog: boolean;
    isEditMode: boolean;
    showViewDialog: boolean;
    selectedBillTariff: BillTariffModel | null;
    maxVersionsByBillTypeCode: Map<string, number>; // Map to store max version for each BillTypeCode
    isInitialLoad: boolean; // Track if this is the initial load to apply default filter
}

const tariffTypeOptions = [
    { label: "PERCENTAGE", value: "PERCENTAGE" },
    { label: "FIXED", value: "FIXED" },
    { label: "SCALE", value: "SCALE" },
];

const emptyScaleTier: ScaleTier = { minValue: 0, maxValue: 0, tariffAmount: 0 };

export default class BillTariff extends Component<Record<string, never>, BillTariffState> {
    private dataTable: ExtendedDataTable | null = null;

    constructor(props: Record<string, never> | Readonly<Record<string, never>>) {
        super(props);

        this.state = {
            billTariffs: [],
            isLoading: false,
            totalRecords: 0,
            showDialog: false,
            isEditMode: false,
            showViewDialog: false,
            selectedBillTariff: null,
            maxVersionsByBillTypeCode: new Map<string, number>(),
            isInitialLoad: true,
        };
    }

    componentDidMount() {
        // Initial load will be triggered by ExtendedDataTable's onChange
    }

    // Calculate maximum version for each BillTypeCode
    calculateMaxVersions = (billTariffs: BillTariffModel[]): Map<string, number> => {
        const maxVersions = new Map<string, number>();

        billTariffs.forEach((tariff) => {
            const currentMax = maxVersions.get(tariff.billTypeCode) || 0;
            if (tariff.version > currentMax) {
                maxVersions.set(tariff.billTypeCode, tariff.version);
            }
        });

        return maxVersions;
    };

    // Check if a bill tariff has the maximum version
    isLatestVersion = (billTariff: BillTariffModel): boolean => {
        const maxVersion = this.state.maxVersionsByBillTypeCode.get(billTariff.billTypeCode);
        return maxVersion !== undefined && billTariff.version === maxVersion;
    };

    // Status filter template with dropdown
    statusFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
        // Determine current value: "true" or "false"
        let currentValue: string | null = null;
        if (options.value === "true" || options.value === true) {
            currentValue = "true";
        } else if (options.value === "false" || options.value === false) {
            currentValue = "false";
        } else {
            currentValue = null; // No selection
        }

        return (
            <Dropdown
                value={currentValue}
                options={[
                    { label: "Active", value: "true" },
                    { label: "Inactive", value: "false" },
                ]}
                onChange={(e: any) => {
                    options.filterApplyCallback(e.value);
                }}
                placeholder="Select Status"
                showClear
                className="p-column-filter"
            />
        );
    };

    // Load bill tariffs with server-side pagination, filtering, and sorting
    loadBillTariffsData = async (searchRequest: SearchRequestBaseModel) => {
        // No default filter - show all records by default
        if (this.state.isInitialLoad) {
            this.setState({ isInitialLoad: false });
        }

        this.setState({ isLoading: true });

        try {
            const result = await tariffApiService.getAllBillTariffsPaged(searchRequest);

            if (!result.success) {
                this.setState({ isLoading: false });
                toastService.error(result.message);
                return;
            }

            const billTariffs = result.data || [];
            const maxVersions = this.calculateMaxVersions(billTariffs);

            this.setState({
                billTariffs: billTariffs,
                totalRecords: result.totalCount || 0,
                maxVersionsByBillTypeCode: maxVersions,
                isLoading: false,
            });
        } catch (error) {
            this.setState({ isLoading: false });
            toastService.error("Failed to load bill tariffs");
        }
    };

    openCreateDialog = () => {
        this.setState({
            showDialog: true,
            isEditMode: false,
            selectedBillTariff: null,
        });
    };

    openEditDialog = (billTariff: BillTariffModel) => {
        this.setState({
            showDialog: true,
            isEditMode: true,
            selectedBillTariff: billTariff,
        });
    };

    closeDialog = () => {
        this.setState({
            showDialog: false,
            isEditMode: false,
            selectedBillTariff: null,
        });
    };

    openViewDialog = (billTariff: BillTariffModel) => {
        this.setState({
            showViewDialog: true,
            selectedBillTariff: billTariff,
        });
    };

    closeViewDialog = () => {
        this.setState({
            showViewDialog: false,
            selectedBillTariff: null,
        });
    };

    // Form validation and operations moved to EditBillTariffModal component
    // All form-related methods have been removed

    handleDelete = (billTariff: BillTariffModel) => {
        confirmAction(
            `Are you sure you want to delete Bill Tariff for ${billTariff.billTypeCode}?`,
            "Confirm Delete",
            async () => {
                this.setState({ isLoading: true });
                const result = await tariffApiService.deleteBillTariff(billTariff.id);

                if (!result.success) {
                    toastService.error(result.message);
                    this.setState({ isLoading: false });
                    return;
                }

                toastService.success("Bill Tariff deleted successfully");

                // Reset table to reload data with current filters/pagination
                if (this.dataTable) {
                    this.dataTable.reset();
                }
            },
        );
    };

    handleToggleStatus = async (billTariff: BillTariffModel) => {
        // Allow status toggle for any version (backend will handle validation)
        this.setState({ isLoading: true });
        const result = await tariffApiService.toggleBillTariffStatus(billTariff.id);

        if (!result.success) {
            toastService.error(result.message);
            this.setState({ isLoading: false });
            return;
        }

        toastService.success("Bill Tariff status toggled successfully");

        // Reset table to reload data with current filters/pagination
        if (this.dataTable) {
            this.dataTable.reset();
        }
    };

    formatValueDisplay = (rowData: BillTariffModel): { text: string; tooltip?: string } => {
        if (rowData.tariffType === "SCALE") {
            try {
                const tiers = JSON.parse(rowData.value) as ScaleTier[];
                return {
                    text: `${tiers.length} tier(s)`,
                    tooltip: rowData.value,
                };
            } catch {
                return { text: rowData.value };
            }
        }
        return { text: rowData.value };
    };

    statusToggleTemplate = (rowData: BillTariffModel) => {
        return (
            <div style={{ display: "flex", flexDirection: "row", alignItems: "center", padding: "5px" }} onClick={(e) => e.stopPropagation()}>
                <div style={{ flex: 1 }}>
                    <InputSwitch
                        checked={rowData.status}
                        onChange={(e) => {
                            e.stopPropagation();
                            this.handleToggleStatus(rowData);
                        }}
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            </div>
        );
    };

    actionBodyTemplate = (rowData: BillTariffModel) => {
        return (
            <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); this.openViewDialog(rowData); }}
                    className="p-1.5 rounded-md text-neutral-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:text-primary-400 dark:hover:bg-primary-900/20 transition-colors"
                    title="View"
                >
                    <Eye size={15} />
                </button>
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); this.openEditDialog(rowData); }}
                    className="p-1.5 rounded-md text-neutral-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:text-primary-400 dark:hover:bg-primary-900/20 transition-colors"
                    title="Edit"
                >
                    <Pencil size={15} />
                </button>
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); this.handleDelete(rowData); }}
                    className="p-1.5 rounded-md text-neutral-400 hover:text-danger-600 hover:bg-danger-50 dark:hover:text-danger-400 dark:hover:bg-danger-900/20 transition-colors"
                    title="Delete"
                >
                    <Trash2 size={15} />
                </button>
            </div>
        );
    };

    renderHeader = (hasActiveFilters: boolean) => (
        <DataTableToolbar
            onClear={() => { this.setState({ isInitialLoad: true }); this.dataTable!.reset(); }}
            addLabel="Create Bill Tariff"
            onAdd={this.openCreateDialog}
            hasActiveFilters={hasActiveFilters}
        />
    );

    render() {

        return (
            <Fragment>
                <ExtendedDataTable
                    ref={(ref) => (this.dataTable = ref)}
                    header={this.renderHeader}
                    loading={this.state.isLoading}
                    value={this.state.billTariffs}
                    onChange={this.loadBillTariffsData}
                    totalRecords={this.state.totalRecords}
                    defaultSortOrder={SortOrderType.Ascending}
                    defaultSortField="BillTypeCode"
                    onRowClick={(e) => {
                        // Open view dialog when row is clicked
                        // Action buttons use stopPropagation() to prevent this event
                        if (e.data) {
                            this.openViewDialog(e.data);
                        }
                    }}
                >
                    <Column
                        {...({ alwaysVisible: true } as any)}
                        field="billTypeCode"
                        header="Bill Type Code"
                        filterMatchModeOptions={stringFilterMatchModeOptions}
                        filterMatchMode="Equals"
                        body={(rowData: BillTariffModel) => {
                            return (
                                <p>{rowData.billTypeCode}</p>
                            );
                        }}
                        headerStyle={{ minWidth: "200px" }}
                    ></Column>
                    <Column
                        {...({ alwaysVisible: true } as any)}
                        field="tariffType"
                        header="Tariff Type"
                        body={(rowData: BillTariffModel) => {
                            return (
                                <p>{rowData.tariffType}</p>
                            );
                        }}
                        headerStyle={{ minWidth: "120px" }}
                    ></Column>
                    <Column
                        field="value"
                        header="Value"
                        body={(rowData: BillTariffModel) => {
                            const display = this.formatValueDisplay(rowData);
                            if (display.tooltip) {
                                const tooltipId = `value-tooltip-${rowData.id}`;
                                return (
                                    <>
                                        <p
                                            className={tooltipId}
                                            style={{ cursor: "pointer", textDecoration: "underline dotted" }}
                                        >
                                            {display.text}
                                        </p>
                                        <Tooltip target={`.${tooltipId}`} content={display.tooltip} position="top" />
                                    </>
                                );
                            }
                            return (
                                <p>{display.text}</p>
                            );
                        }}
                        headerStyle={{ minWidth: "100px" }}
                    ></Column>
                    <Column
                        field="currency"
                        header="Currency"
                        body={(rowData: BillTariffModel) => {
                            return (
                                <p>{rowData.currency || "-"}</p>
                            );
                        }}
                        sortable={false}
                        headerStyle={{ minWidth: "100px" }}
                    ></Column>
                    <Column
                        field="version"
                        header="Version"
                        body={(rowData: BillTariffModel) => {
                            return (
                                <p>{rowData.version}</p>
                            );
                        }}
                        headerStyle={{ minWidth: "100px" }}
                    ></Column>
                    <Column
                        field="effectiveFrom"
                        header="Effective From"
                        body={(rowData: BillTariffModel) => (
                            <p>{moment(rowData.effectiveFrom).format("DD/MM/YYYY")}</p>
                        )}
                        dataType="date"
                        filterElement={dateFilterTemplate}
                        filterMatchModeOptions={numberFilterMatchModeOptions}
                        filterMatchMode="Equals"
                        headerStyle={{ minWidth: "200px" }}
                    ></Column>
                    <Column
                        field="effectiveTo"
                        header="Effective To"
                        body={(rowData: BillTariffModel) =>
                            rowData.effectiveTo ? <p>{moment(rowData.effectiveTo).format("DD/MM/YYYY")}</p> : <p>-</p>
                        }
                        headerStyle={{ minWidth: "120px" }}
                    ></Column>
                    <Column
                        field="createdTimeStamp"
                        header="Created On"
                        body={dateBodyTemplate("createdTimeStamp")}
                        headerStyle={{ minWidth: "120px" }}
                    ></Column>
                    <Column
                        field="modifiedTimeStamp"
                        header="Modified On"
                        body={dateBodyTemplate("modifiedTimeStamp")}
                        headerStyle={{ minWidth: "150px" }}
                    ></Column>
                    <Column
                        field="status"
                        header="Status"
                        body={this.statusToggleTemplate}
                        filterMatchModeOptions={statusFilterMatchModeOptions}
                        filterElement={this.statusFilterTemplate}
                        filterMatchMode="Equals"
                    ></Column>
                    <Column
                        body={this.actionBodyTemplate}
                        headerStyle={{ width: "12em", textAlign: "center" }}
                        bodyStyle={{ textAlign: "center", overflow: "visible" }}
                    />
                </ExtendedDataTable>


                <EditBillTariffModal
                    visible={this.state.showDialog}
                    isEditMode={this.state.isEditMode}
                    selectedBillTariff={this.state.selectedBillTariff}
                    onHide={this.closeDialog}
                    onSuccess={() => {
                        // Reset table to reload data with current filters/pagination
                        if (this.dataTable) {
                            this.dataTable.reset();
                        }
                    }}
                />

                <ViewBillTariffModal
                    visible={this.state.showViewDialog}
                    billTariff={this.state.selectedBillTariff}
                    onHide={this.closeViewDialog}
                />
            </Fragment>
        );
    }
}

